import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  styled as muiStyled,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { CreateButton } from '../../../components/buttons/CreateButton';
import StatusChip from '../../../components/chips/StatusChip';
import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useDeleteReportedPostById } from '../../../lib/post/hooks';
import useGetAllReportedPosts from '../../../lib/post/hooks/useGetAllReportedPosts';
import { SortDirection } from '../../../lib/util/helpers';

const ReportedPostsList = () => {
  const navigate = useNavigate();
  const [optionsUpdated, setOptionsUpdated] = useState(true);
  const handleAllPosts = () => navigate(`/cms/posts`);
  const handleEditReportedPost = (id: number) => navigate(`/cms/posts/reported/${id}`);
  const { mutateAsync: deleteReportedPost, isLoading: isSubmitting } = useDeleteReportedPostById();

  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: reportedPost,
    isLoading,
    error,
  } = useGetAllReportedPosts({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  useEffect(() => {
    if (!optionsUpdated) {
      return;
    }
    setOptionsUpdated(false);
  }, [optionsUpdated, page, sort, search]);

  useEffect(() => {
    setOptionsUpdated(true);
  }, [setOptionsUpdated, page, sort, search]);

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Date',
      name: 'post.lastReportedDate',
    },
    {
      text: 'Post ID',
      name: 'post.id',
    },
    {
      text: 'Title',
      name: 'post.title',
    },
    {
      text: 'Reported Post Count',
      name: 'post_report_count',
    },
    {
      text: 'Status',
      name: 'post.deactivated',
    },
    {
      text: 'Actions',
      name: 'actions',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Reported Articles
        </Typography>
        <CreateButton variant="contained" onClick={handleAllPosts}>
          Back to Articles List
        </CreateButton>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              reportedPost.data &&
              reportedPost.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index}>
                    <TableCell>{format(new Date(row.lastReportedDate), 'MM/dd/yyyy hh:mm aaa')}</TableCell>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.reportCount}</TableCell>
                    <TableCell>
                      <StatusChip deactivated={Boolean(row.deactivated)} />
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditReportedPost(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                            Delete
                          </Button>
                        )}
                        title="Delete Reported Article"
                        description="Are you sure you want to delete this reported article?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deleteReportedPost(row.id);
                          close();
                        }}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {reportedPost.data.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No reported posts records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {reportedPost.total !== null && reportedPost.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {reportedPost.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(reportedPost.total / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default ReportedPostsList;
